import { useEffect, useId, useRef, useState } from "react";
import { readClipboardImages, validateAttachments, type AttachmentPolicy } from "../utils/attachments";
import "./attachment-picker.css";

interface AttachmentPickerProps extends AttachmentPolicy {
  label: string;
  inputId?: string;
  disabled?: boolean;
  busy?: boolean;
  files?: File[];
  onFiles: (files: File[]) => void | Promise<void>;
  onRemove?: (index: number) => void;
  help?: string;
  showLabel?: boolean;
}

function AttachmentPreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type)) return;
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url ? <img className="attachment-picker__preview" src={url} alt="" /> : null;
}

export function AttachmentPicker({
  label, inputId, accept, multiple = false, maxBytes = 20 * 1024 * 1024, maxFiles,
  disabled = false, busy = false, files = [], onFiles, onRemove, help, showLabel = true,
}: AttachmentPickerProps) {
  const uid = useId();
  const id = inputId ?? `attachment-${uid}`;
  const [error, setError] = useState<string>();
  const [reading, setReading] = useState(false);
  const alive = useRef(true);
  const inFlight = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  // Read the latest props after async permission prompts, including a changed record.
  const latest = useRef({ disabled, busy, onFiles });
  latest.current = { disabled, busy, onFiles };
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const locked = disabled || busy || reading;

  async function receive(selected: File[]) {
    if (!selected.length || latest.current.disabled || latest.current.busy || inFlight.current) return;
    const validation = validateAttachments(selected, { accept, multiple, maxBytes, maxFiles });
    setError(validation);
    if (validation) return;
    inFlight.current = true;
    try { await latest.current.onFiles(selected); }
    catch { if (alive.current) setError("첨부를 처리하지 못했습니다. 화면의 오류 안내를 확인하고 다시 시도해주세요."); }
    finally { inFlight.current = false; }
  }

  async function pasteFromClipboard() {
    if (locked || inFlight.current) return;
    setReading(true);
    setError(undefined);
    try {
      const selected = await readClipboardImages(navigator.clipboard);
      if (alive.current) await receive(selected);
    } catch (failure) {
      if (alive.current) setError(failure instanceof Error ? failure.message : "클립보드 이미지를 읽지 못했습니다.");
    } finally { if (alive.current) setReading(false); }
  }

  return <div className="attachment-picker" role="group" aria-label="파일과 클립보드 첨부" aria-disabled={locked} aria-busy={busy || reading}
    tabIndex={disabled ? -1 : 0} aria-describedby={`${id}-help`}
    onPaste={(event) => {
      const selected = Array.from(event.clipboardData.files);
      if (!selected.length) return;
      event.preventDefault();
      if (!locked) void receive(selected);
    }}
    onDragOver={(event) => { if (Array.from(event.dataTransfer.types).includes("Files")) event.preventDefault(); }}
    onDrop={(event) => { if (!event.dataTransfer.files.length) return; event.preventDefault(); if (!locked) void receive(Array.from(event.dataTransfer.files)); }}>
    {showLabel && <span>{label}</span>}
    <div className="attachment-picker__actions">
      <button type="button" disabled={locked} onClick={() => input.current?.click()}>파일 선택</button>
      <button type="button" disabled={locked} onClick={() => void pasteFromClipboard()}>{reading ? "클립보드 읽는 중…" : "클립보드 붙여넣기"}</button>
      <input ref={input} id={id} aria-label={label} type="file" accept={accept} multiple={multiple} disabled={locked}
        onChange={(event) => { const selected = Array.from(event.currentTarget.files ?? []); event.currentTarget.value = ""; void receive(selected); }} />
    </div>
    <p id={`${id}-help`} className="attachment-picker__help">{help ? `${help} · ` : ""}이미지 복사 후 이 영역에서 ⌘V / Ctrl+V</p>
    {files.length > 0 && <ul className="attachment-picker__files">{files.map((file, index) => <li key={`${file.name}-${file.lastModified}-${index}`}>
      <AttachmentPreview file={file} /><span>{file.name}</span>
      {onRemove && <button type="button" disabled={locked} aria-label={`${file.name} 제거`} onClick={() => onRemove(index)}>제거</button>}
    </li>)}</ul>}
    {busy && <p className="attachment-picker__help" role="status">첨부 처리 중…</p>}
    {error && <p className="attachment-picker__error" role="alert">{error}</p>}
  </div>;
}
